/**
 * VideoThumbnailService
 *
 * Shared client-side capture of a single video frame, resized + JPEG-compressed
 * into a video THUMBNAIL — the work a video Cloud Function used to do with an
 * ffmpeg single-frame pass (`scale=...480...`).
 *
 * This is a feature-neutral, generic frame-capture service consumed by BOTH the
 * chat and community video pipelines:
 *   - `PrepareChatVideoThumbnailUseCase` (chat media is client-side E2EE, so the
 *     server can never see plaintext to thumbnail — the client captures the
 *     frame, then encrypts it before upload).
 *   - `PrepareCommunityVideoThumbnailUseCase` (community media is not E2EE — the
 *     captured plaintext frame is uploaded directly).
 *
 * Frame capture from a video is a distinct responsibility (analogous to, but not
 * the same as, `ChatImageProcessingService`'s still-image resize). The thumbnail
 * rendition is analogous to the chat-image thumbnail, so it reuses
 * `CHAT_IMAGE_PROCESSING_CONSTANTS` (the ≈480 longest-side target + JPEG quality
 * + MIME) rather than redefining those values. Capture parameters are unchanged
 * across consumers.
 *
 * Web-first design — no Flutter parity for this path (Flutter's video pipeline is
 * a separate repo). The resize target approximates the server ffmpeg
 * single-frame 480 scale.
 *
 * CODEC-LIMIT FALLBACK (consideration D): a browser may not be able to decode
 * the uploaded video's codec at all. In that case frame capture fails; this
 * service returns `null` (NOT a throw) so the use case skips the encrypt +
 * upload and the message simply has no `thumbnail` — the bubble/card renders the
 * placeholder frame + play icon.
 *
 * TESTABILITY: the DOM/`<video>`/canvas frame-capture step is injected via the
 * constructor (`captureFrame`). The default uses a real offscreen `<video>` +
 * `<canvas>`; tests inject a lightweight fake so the orchestration (and the
 * null-on-failure contract) can be exercised under happy-dom, which has no real
 * video decoder.
 */

import { appLogger } from '@/domain/services/analytics/appLogger';
import { CHAT_IMAGE_PROCESSING_CONSTANTS } from '@/domain/constants/chatImageProcessingConstants';

/**
 * Capture a single frame from `source`, resized to fit within a `maxSide`
 * longest-side box (aspect preserved, never upscaled), and export it as a JPEG
 * Blob at `quality`.
 *
 * Isolated behind this type so tests can replace the `<video>`/canvas raster
 * step (which has no decoder under happy-dom). Implementations MUST reject (or
 * the default does) when the browser cannot decode the codec; the service turns
 * that rejection into the `null` fallback.
 */
export type CaptureVideoFrameFn = (
  source: Blob,
  maxSide: number,
  quality: number,
  mimeType: string,
) => Promise<Blob>;

/**
 * Compute the scaled-down dimensions for a `maxSide` longest-side box,
 * preserving aspect ratio and NEVER upscaling — mirrors the ffmpeg
 * `force_original_aspect_ratio=decrease` the server thumbnail pass used.
 */
function fitWithin(
  width: number,
  height: number,
  maxSide: number,
): { width: number; height: number } {
  const longest = Math.max(width, height);
  // `decrease`: only ever shrink (scale <= 1). Guard a zero/unknown size.
  const scale = longest > 0 ? Math.min(1, maxSide / longest) : 1;
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

/**
 * Default capture: load `source` into an offscreen muted `<video>`, seek to an
 * early frame, draw the decoded frame onto a `<canvas>` resized to the box, and
 * export `image/jpeg`. Rejects if the browser cannot decode the codec (the
 * `error` event) — the service converts that to the `null` fallback.
 */
const defaultCaptureFrame: CaptureVideoFrameFn = (
  source: Blob,
  maxSide: number,
  quality: number,
  mimeType: string,
): Promise<Blob> => {
  return new Promise<Blob>((resolve, reject) => {
    const objectUrl = URL.createObjectURL(source);
    const video = document.createElement('video');
    let settled = false;

    const cleanup = (): void => {
      video.removeAttribute('src');
      try {
        video.load();
      } catch {
        // load() can throw on some teardown paths — ignore; we are releasing.
      }
      URL.revokeObjectURL(objectUrl);
    };

    const fail = (error: Error): void => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(error);
    };

    const succeed = (blob: Blob): void => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(blob);
    };

    // The browser cannot decode this codec → fall through to the null fallback.
    video.onerror = (): void => fail(new Error('VideoThumbnailService: video decode/codec error'));

    video.onloadeddata = (): void => {
      // Seek to an early-but-non-zero frame (the very first frame is often a
      // black/blank keyframe). Clamp to the clip duration for short videos.
      const target = Math.min(0.1, (video.duration || 0) / 2);
      try {
        video.currentTime = target;
      } catch (error) {
        fail(error as Error);
      }
    };

    video.onseeked = (): void => {
      try {
        const { width, height } = fitWithin(
          video.videoWidth,
          video.videoHeight,
          maxSide,
        );
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          fail(new Error('VideoThumbnailService: failed to acquire 2D canvas context'));
          return;
        }
        ctx.drawImage(video, 0, 0, width, height);
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              fail(new Error('VideoThumbnailService: canvas.toBlob returned null'));
              return;
            }
            succeed(blob);
          },
          mimeType,
          quality,
        );
      } catch (error) {
        fail(error as Error);
      }
    };

    video.muted = true;
    video.preload = 'auto';
    video.src = objectUrl;
  });
};

export class VideoThumbnailService {
  private readonly captureFrame: CaptureVideoFrameFn;

  constructor(deps?: { captureFrame?: CaptureVideoFrameFn }) {
    this.captureFrame = deps?.captureFrame ?? defaultCaptureFrame;
  }

  /**
   * Capture a thumbnail frame from `source` and return it as a JPEG Blob.
   *
   * Resizes to the SAME longest-side target as the chat-image thumbnail
   * (`THUMBNAIL_MAX_SIDE` ≈ 480, mirroring the server ffmpeg single-frame
   * pass), aspect preserved, never upscaled.
   *
   * @param source - The user-selected chat/community video File/Blob.
   * @returns the thumbnail JPEG Blob, or `null` when the browser cannot decode
   *   the codec / frame capture fails (consideration D). A `null` return is NOT
   *   an error: the caller skips encrypt+upload and the message has no
   *   `thumbnail`, so the bubble/card shows the placeholder frame + play icon.
   */
  async capture(source: Blob): Promise<Blob | null> {
    try {
      return await this.captureFrame(
        source,
        CHAT_IMAGE_PROCESSING_CONSTANTS.THUMBNAIL_MAX_SIDE,
        CHAT_IMAGE_PROCESSING_CONSTANTS.THUMBNAIL_JPEG_QUALITY,
        CHAT_IMAGE_PROCESSING_CONSTANTS.OUTPUT_MIME_TYPE,
      );
    } catch (error) {
      // Consideration D: the browser could not produce a frame (unsupported
      // codec, decode error, …). This is an expected, recoverable outcome — log
      // it and return null so the send flow falls back to the placeholder. We
      // do NOT rethrow: a missing video thumbnail must not abort the send.
      appLogger.error(
        'Failed to capture video thumbnail frame (codec/decode) — falling back to null',
        'VideoThumbnailService.capture',
        { error: error as Error },
      );
      return null;
    }
  }
}

/** Shared singleton — mirrors the other domain-service singletons. */
export const videoThumbnailService = new VideoThumbnailService();
